import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import { useSettings } from '@/context/SettingsContext';
import axios from 'axios';
import { 
  Clock,
  CheckCircle,
  XCircle,
  ArrowCounterClockwise,
  Bell,
  Fire,
  Timer,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

export const KDSPage = () => {
  const { currentOutlet } = useApp();
  const { settings } = useSettings();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevOrderIdsRef = useRef(new Set());
  const audioCtxRef = useRef(null);

  const fetchKDSData = useCallback(async () => {
    if (!currentOutlet) return;
    try {
      let url = `${API_URL}/api/kds/orders?outlet_id=${currentOutlet.id}`;
      if (selectedStation) url += `&station=${selectedStation}`;
      
      const [ordersRes, statsRes, stationsRes] = await Promise.all([
        axios.get(url, { withCredentials: true }),
        axios.get(`${API_URL}/api/kds/stats?outlet_id=${currentOutlet.id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/kds-stations?outlet_id=${currentOutlet.id}`, { withCredentials: true }),
      ]);
      
      const newOrders = ordersRes.data || [];
      // Detect newly-arrived orders and play a chime if enabled in settings
      const prevIds = prevOrderIdsRef.current;
      const arrivedIds = newOrders.filter((o) => !prevIds.has(o.id)).map((o) => o.id);
      const kdsCfg = settings?.kds;
      if (prevIds.size > 0 && arrivedIds.length > 0 && kdsCfg?.sound_enabled) {
        try {
          if (!audioCtxRef.current) {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (Ctx) audioCtxRef.current = new Ctx();
          }
          const ctx = audioCtxRef.current;
          if (ctx) {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const vol = Math.max(0, Math.min(1, (kdsCfg.sound_volume ?? 70) / 100));
            osc.type = 'sine';
            osc.frequency.setValueAtTime(880, ctx.currentTime);
            osc.frequency.linearRampToValueAtTime(1320, ctx.currentTime + 0.18);
            gain.gain.setValueAtTime(vol * 0.6, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
            osc.connect(gain).connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.5);
          }
        } catch (e) { /* audio not available — ignore */ }
      }
      prevOrderIdsRef.current = new Set(newOrders.map((o) => o.id));

      setOrders(newOrders);
      setStats(statsRes.data);
      setStations(stationsRes.data);
    } catch (e) {
      console.error('Error fetching KDS data:', e);
    } finally {
      setLoading(false);
    }
  }, [currentOutlet, selectedStation, settings?.kds?.sound_enabled, settings?.kds?.sound_volume]);

  useEffect(() => {
    fetchKDSData();
    const interval = setInterval(fetchKDSData, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [fetchKDSData]);

  // Heartbeat: ping the selected station every 20s so routing knows the KDS is alive.
  // If no station is selected, ping all kitchen stations belonging to the outlet.
  useEffect(() => {
    if (!currentOutlet || stations.length === 0) return;

    const sendHeartbeats = async () => {
      const targets = selectedStation
        ? stations.filter((s) => s.station_type === selectedStation || s.name === selectedStation)
        : stations;
      await Promise.all(
        targets.map((s) =>
          axios
            .post(`${API_URL}/api/kds-stations/${s.id}/heartbeat`, {}, { withCredentials: true })
            .catch(() => null)
        )
      );
    };

    sendHeartbeats();
    const t = setInterval(sendHeartbeats, 20000); // every 20s (well under 30s threshold)
    return () => clearInterval(t);
  }, [currentOutlet, stations, selectedStation]);

  // Live WebSocket: refresh KDS instantly when backend broadcasts a new_order / status event
  useEffect(() => {
    if (!currentOutlet) return;
    const wsUrl = API_URL.replace(/^http/, 'ws') + `/ws/kds_${currentOutlet.id}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg && (msg.type === 'new_order' || msg.type === 'item_status')) {
            fetchKDSData();
          }
        } catch (e) {
          // ignore
        }
      };
    } catch (e) {
      console.warn('KDS WS failed', e);
    }
    return () => {
      try { ws && ws.close(); } catch (e) {}
    };
  }, [currentOutlet, fetchKDSData]);

  const getTicketAge = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMinutes = Math.floor((now - created) / 60000);
    return diffMinutes;
  };

  const getTicketUrgency = (createdAt) => {
    const age = getTicketAge(createdAt);
    if (age >= 15) return 'urgent';
    if (age >= 10) return 'warning';
    return 'normal';
  };

  const handleBumpItem = async (orderId, itemIndex) => {
    try {
      await axios.patch(
        `${API_URL}/api/orders/${orderId}/items/${itemIndex}/status?status=ready`,
        {},
        { withCredentials: true }
      );
      fetchKDSData();
    } catch (e) {
      console.error('Error bumping item:', e);
    }
  };

  const handleBumpOrder = async (orderId) => {
    try {
      await axios.patch(
        `${API_URL}/api/orders/${orderId}/status?status=ready`,
        {},
        { withCredentials: true }
      );
      fetchKDSData();
    } catch (e) {
      console.error('Error bumping order:', e);
    }
  };

  const handleRecallOrder = async (orderId) => {
    try {
      await axios.patch(
        `${API_URL}/api/orders/${orderId}/status?status=preparing`,
        {},
        { withCredentials: true }
      );
      fetchKDSData();
    } catch (e) {
      console.error('Error recalling order:', e);
    }
  };

  if (!currentOutlet) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an outlet to view KDS</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="kds-page">
      {/* Header Stats */}
      <div className="bg-white rounded-lg border border-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div>
              <p className="text-sm text-muted-foreground">Open Tickets</p>
              <p className="text-3xl font-heading font-bold text-primary">{stats?.total_open || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Completed Today</p>
              <p className="text-3xl font-heading font-bold text-accent-green">{stats?.total_completed || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Items by Station</p>
              <div className="flex gap-4 mt-1">
                {Object.entries(stats?.items_by_station || {}).map(([station, count]) => (
                  <span key={station} className="text-sm">
                    <span className="font-medium capitalize">{station}:</span> {count}
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          {/* Station Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedStation(null)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                !selectedStation ? 'bg-primary text-white' : 'bg-surface hover:bg-secondary'
              }`}
            >
              All Stations
            </button>
            {stations.map((station) => (
              <button
                key={station.id}
                onClick={() => setSelectedStation(station.name.toLowerCase().split(' ')[0])}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedStation === station.name.toLowerCase().split(' ')[0]
                    ? 'bg-primary text-white' 
                    : 'bg-surface hover:bg-secondary'
                }`}
              >
                {station.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KDS Tickets Grid */}
      <ScrollArea className="h-[calc(100vh-16rem)]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {orders.map((order) => {
            const urgency = getTicketUrgency(order.created_at);
            const age = getTicketAge(order.created_at);
            
            return (
              <div
                key={order.id}
                className={`kds-ticket ${
                  urgency === 'urgent' ? 'kds-ticket-urgent' :
                  urgency === 'warning' ? 'kds-ticket-warning' :
                  'kds-ticket-normal'
                }`}
                data-testid={`kds-ticket-${order.order_number}`}
              >
                {/* Ticket Header */}
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-border">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-heading font-bold">#{order.token_number}</span>
                      {urgency !== 'normal' && (
                        <Fire 
                          size={20} 
                          className={urgency === 'urgent' ? 'text-accent-red animate-pulse' : 'text-accent-yellow'} 
                          weight="fill"
                        />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground capitalize">{order.order_type.replace('_', ' ')}</p>
                  </div>
                  <div className="text-right">
                    <div className={`flex items-center gap-1 text-sm font-medium ${
                      urgency === 'urgent' ? 'text-accent-red' :
                      urgency === 'warning' ? 'text-accent-yellow' :
                      'text-muted-foreground'
                    }`}>
                      <Timer size={14} />
                      <span>{age}m</span>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div className="space-y-2 mb-4">
                  {order.pending_items?.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-start justify-between bg-white rounded-lg p-2"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg">{item.quantity}x</span>
                          <span className="font-medium">{item.menu_item_name}</span>
                        </div>
                        {item.modifiers?.length > 0 && (
                          <div className="ml-6 mt-1 space-y-0.5">
                            {item.modifiers.map((mod, i) => (
                              <p key={i} className="text-xs text-primary">
                                + {mod.option_name}
                              </p>
                            ))}
                          </div>
                        )}
                        {item.notes && (
                          <p className="ml-6 text-xs text-accent-red mt-1">Note: {item.notes}</p>
                        )}
                        <p className="ml-6 text-xs text-muted-foreground mt-1 capitalize">
                          Station: {item.kitchen_station}
                        </p>
                      </div>
                      <button
                        onClick={() => handleBumpItem(order.id, order.items.findIndex(i => i.menu_item_name === item.menu_item_name))}
                        className="p-2 bg-accent-green/10 rounded-lg hover:bg-accent-green/20 transition-colors"
                        data-testid={`bump-item-${order.id}-${index}`}
                      >
                        <CheckCircle size={20} className="text-accent-green" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Customer Info */}
                {order.customer_name && (
                  <p className="text-sm text-muted-foreground mb-3">
                    Customer: {order.customer_name}
                  </p>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-3 border-t border-border">
                  <Button
                    onClick={() => handleBumpOrder(order.id)}
                    className="flex-1 h-10 bg-accent-green hover:bg-green-600 text-white"
                    data-testid={`bump-order-${order.id}`}
                  >
                    <CheckCircle size={18} className="mr-2" />
                    Bump All
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleRecallOrder(order.id)}
                    className="h-10"
                    data-testid={`recall-order-${order.id}`}
                  >
                    <ArrowCounterClockwise size={18} />
                  </Button>
                </div>
              </div>
            );
          })}

          {orders.length === 0 && !loading && (
            <div className="col-span-full flex flex-col items-center justify-center py-16 text-muted-foreground">
              <CheckCircle size={64} className="mb-4 opacity-30" />
              <p className="text-xl font-medium">All caught up!</p>
              <p className="text-sm">No pending orders</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default KDSPage;
